import React, { useState, useContext } from "react";
import { X, Check, AlertCircle, Calendar, CreditCard, Receipt, FileText, User, Building, Clock, ChevronRight, ShieldCheck } from "lucide-react";
import Avatar from "./Avatar";
import { AppContext } from "../context/AppContext";

const ApprovalActionModal = ({ approval, onClose, onAction, currentUser, isMyTurn: propIsMyTurn }) => {
  const { users = [], approvals = [], withdrawLeaveRequest, withdrawExpenseRequest } = useContext(AppContext) || {};
  const [comment, setComment] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  if (!approval) return null;

  const isLeave = approval.Type === "LEAVE";
  const isExpense = approval.Type === "EXPENSE";

  const applicantUser = users.find(u => u.UserID === approval.ApplicantID || u.Name === approval.ApplicantName) || {};
  const applicantTotalLeave = (applicantUser.TotalLeave !== undefined && applicantUser.TotalLeave !== null && applicantUser.TotalLeave !== '') ? Number(applicantUser.TotalLeave) : 15;
  const applicantUsedLeave = Number(applicantUser.UsedLeave || 0);
  const applicantBaseRemaining = (applicantUser.RemainingLeave !== undefined && applicantUser.RemainingLeave !== null && applicantUser.RemainingLeave !== '') ? Number(applicantUser.RemainingLeave) : (applicantTotalLeave - applicantUsedLeave);

  const isApplicant = Boolean(
    currentUser && (
      currentUser.UserID === approval.ApplicantID ||
      currentUser.Name === approval.ApplicantName ||
      (currentUser.Email && currentUser.Email.toLowerCase() === (approval.ApplicantEmail || "").toLowerCase())
    )
  );

  const otherPendingList = (approvals || []).filter(a => 
    a.Type === 'LEAVE' && 
    a.Status === 'PENDING' && 
    a.ApprovalID !== approval.ApprovalID &&
    (a.ApplicantID === approval.ApplicantID || a.ApplicantName === approval.ApplicantName)
  );
  const otherPendingDays = otherPendingList.reduce((s, a) => s + (Number(a.LeaveDays || a.Days) || 0), 0);

  const currentRequestDays = Number(approval.LeaveDays || approval.Days || 0);
  const isPending = approval.Status === 'PENDING';
  const isApproved = approval.Status === 'APPROVED';
  const allPendingDays = otherPendingDays + (isPending ? currentRequestDays : 0);
  const applicantCurrentRemaining = applicantBaseRemaining - otherPendingDays - (isPending ? currentRequestDays : 0);

  const isSpecial = Boolean(approval.IsSpecial);
  const beforeBalance = approval.BeforeLeaveBalance !== undefined
    ? Number(approval.BeforeLeaveBalance)
    : (isApproved
        ? (applicantBaseRemaining + (isSpecial ? 0 : currentRequestDays))
        : (applicantBaseRemaining - otherPendingDays));

  const afterBalance = approval.AfterLeaveBalance !== undefined
    ? Number(approval.AfterLeaveBalance)
    : (isApproved
        ? applicantBaseRemaining
        : (isSpecial ? beforeBalance : (beforeBalance - currentRequestDays)));

  const isCancelReq = approval.Status === 'CANCEL_REQUESTED' || approval.Status === 'CANCELED';
  const restoredBalance = applicantCurrentRemaining + currentRequestDays;

  const currentStep = Number(approval.CurrentStep || 1);
  const stages = approval.calculatedStages || approval.ApprovalStages || [];
  const currentStage = stages.find(s => Number(s.Step) === currentStep);
  const currentApproverName = currentStage?.ApproverName || currentStage?.RoleName || `${currentStep}차 결재자`;

  // Determine if it is strictly current user turn
  const isTurn = propIsMyTurn !== undefined ? Boolean(propIsMyTurn) : (() => {
    if (!currentUser || !currentStage) return false;
    const myId = currentUser.UserID;
    const myName = currentUser.Name;
    const myEmail = (currentUser.Email || "").toLowerCase();
    return (
      currentStage.ApproverID === myId ||
      currentStage.ApproverName === myName ||
      (currentStage.ApproverEmail && currentStage.ApproverEmail.toLowerCase() === myEmail)
    );
  })();

  const handleApprove = async () => {
    setIsSubmitting(true);
    setErrorMessage("");
    try {
      await onAction(approval.ApprovalID, "APPROVED", "", comment.trim());
      onClose();
    } catch (err) {
      setErrorMessage(err.message || "승인 처리 중 오류가 발생했습니다.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!comment.trim()) {
      setErrorMessage("반려 시에는 반드시 반려 사유를 입력해야 합니다.");
      return;
    }
    setIsSubmitting(true);
    setErrorMessage("");
    try {
      await onAction(approval.ApprovalID, "REJECTED", comment.trim(), "");
      onClose();
    } catch (err) {
      setErrorMessage(err.message || "반려 처리 중 오류가 발생했습니다.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4 animate-in fade-in duration-150">
      <div className="bg-white border border-slate-200 rounded-3xl p-5 sm:p-7 max-w-lg w-full shadow-2xl space-y-5 text-left animate-in zoom-in-95 duration-150 max-h-[90vh] overflow-y-auto">
        
        {/* Header */}
        <div className="flex items-center justify-between pb-3 border-b border-slate-100">
          <div className="flex items-center gap-2.5">
            <span className={`px-2.5 py-1 rounded-lg text-xs font-black flex items-center gap-1 ${
              isLeave ? "bg-teal-100 text-teal-800" : isExpense ? "bg-amber-100 text-amber-800" : "bg-indigo-100 text-indigo-800"
            }`}>
              {isLeave ? "🌴 연차/휴가 결재" : isExpense ? "💳 경비 청구 결재" : "📄 일반 결재"}
            </span>
            <span className="text-xs font-bold text-slate-400">#{approval.ApprovalID}</span>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-xl transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Applicant Profile */}
        <div className="bg-slate-50 border border-slate-200/80 rounded-2xl p-3.5 sm:p-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="w-11 h-11 rounded-full overflow-hidden flex-shrink-0 bg-slate-100 flex items-center justify-center border border-slate-200 shadow-2xs">
              <Avatar 
                config={applicantUser.AvatarConfig || applicantUser.ProfileImage} 
                name={approval.ApplicantName || applicantUser.Name || "신청자"} 
                size={44} 
              />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-extrabold text-slate-900 text-sm truncate">
                  {approval.ApplicantName || applicantUser.Name || "신청자"}
                </span>
                {applicantUser.JobTitle && (
                  <span className="text-xs text-slate-500 font-semibold">
                    {applicantUser.JobTitle}
                  </span>
                )}
                <span className="text-[11px] font-bold text-indigo-600 bg-indigo-50 border border-indigo-100 px-2 py-0.5 rounded-md">
                  {approval.ApplicantDept || applicantUser.Department || "부서 미정"}
                </span>
              </div>
              <div className="text-xs text-slate-400 font-medium mt-0.5 truncate">
                신청일시: {approval.CreatedAt || approval.Timestamp ? new Date(approval.CreatedAt || approval.Timestamp).toLocaleString() : "-"}
              </div>
            </div>
          </div>
          <span className={`px-2.5 py-1 rounded-full text-xs font-extrabold border flex-shrink-0 whitespace-nowrap ${
            isTurn ? "bg-rose-50 text-rose-700 border-rose-200" : "bg-amber-50 text-amber-700 border-amber-200"
          }`}>
            {isTurn ? "내 결재 차례" : `${currentStep}차 진행 중`}
          </span>
        </div>

        {/* Request Details */}
        <div className="space-y-3">
          {isLeave && (
            <div className="space-y-3">
              {/* 4-Tier Applicant Leave Overview */}
              <div className="bg-slate-50 p-3 rounded-2xl border border-slate-200 space-y-2">
                <div className="flex justify-between items-center text-xs">
                  <span className="font-extrabold text-slate-800 flex items-center gap-1.5">
                    <span>👤</span> 신청인 연차 보유 현황 ({approval.ApplicantName})
                  </span>
                  <span className="text-[10px] text-slate-400 font-bold">2026 회계연도</span>
                </div>
                <div className="grid grid-cols-4 gap-1.5 text-xs text-center">
                  <div className="bg-white p-2 rounded-xl border border-slate-200/70">
                    <span className="text-[9px] text-slate-400 font-bold block mb-0.5">총 부여</span>
                    <span className="font-black text-xs text-slate-900">{applicantTotalLeave}일</span>
                  </div>
                  <div className="bg-white p-2 rounded-xl border border-slate-200/70">
                    <span className="text-[9px] text-slate-400 font-bold block mb-0.5">사용완료</span>
                    <span className="font-black text-xs text-slate-900">{applicantUsedLeave}일</span>
                  </div>
                  <div className="bg-amber-50/80 p-2 rounded-xl border border-amber-200/70">
                    <span className="text-[9px] text-amber-600 font-bold block mb-0.5">결재대기</span>
                    <span className="font-black text-xs text-amber-600">{allPendingDays}일</span>
                  </div>
                  <div className={`${applicantCurrentRemaining < 0 ? 'bg-rose-50/80 border-rose-200/80' : 'bg-emerald-50/80 border-emerald-200/80'} p-2 rounded-xl border`}>
                    <span className={`text-[9px] ${applicantCurrentRemaining < 0 ? 'text-rose-700' : 'text-emerald-700'} font-bold block mb-0.5`}>실질잔여</span>
                    <span className={`font-black text-xs ${applicantCurrentRemaining < 0 ? 'text-rose-600' : 'text-emerald-600'}`}>{applicantCurrentRemaining.toFixed(1)}일</span>
                  </div>
                </div>
              </div>

              {/* Real-time Calculation Formula Banner */}
              {isCancelReq ? (
                <div className="p-3 bg-gradient-to-r from-emerald-50 via-teal-50 to-sky-50 rounded-2xl border border-emerald-200 space-y-1.5">
                  <div className="flex justify-between items-center text-xs">
                    <span className="font-extrabold text-emerald-900 flex items-center gap-1">
                      <span>🔄</span> 연차 취소 승인 시 복구 계산
                    </span>
                    <span className="text-[10px] font-bold text-emerald-700 bg-white px-2 py-0.5 rounded-md border border-emerald-200">
                      +{currentRequestDays}일 환원
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-1.5 text-xs bg-white p-2 rounded-xl border border-emerald-100 text-center">
                    <div>
                      <span className="text-[9px] text-slate-400 font-bold block">현재 잔여</span>
                      <span className="font-black text-xs text-slate-900">{applicantCurrentRemaining.toFixed(1)}일</span>
                    </div>
                    <div className="border-x border-slate-100">
                      <span className="text-[9px] text-emerald-600 font-bold block">취소 환원</span>
                      <span className="font-black text-xs text-emerald-600">+{currentRequestDays}일</span>
                    </div>
                    <div>
                      <span className="text-[9px] text-emerald-700 font-bold block">복구 후 잔여</span>
                      <span className="font-black text-xs text-emerald-700">{restoredBalance.toFixed(1)}일</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-3 bg-gradient-to-r from-indigo-50 via-sky-50 to-emerald-50 rounded-2xl border border-indigo-100 space-y-1.5">
                  <div className="flex justify-between items-center text-xs">
                    <span className="font-extrabold text-indigo-950 flex items-center gap-1">
                      <span>📊</span> 결재 승인 시 차감 계산
                    </span>
                    <span className="text-[10px] font-bold text-indigo-700 bg-white px-2 py-0.5 rounded-md border border-indigo-200">
                      {isSpecial ? '특별휴가 (미차감)' : `-${currentRequestDays}일 차감`}
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-1.5 text-xs bg-white p-2 rounded-xl border border-indigo-100 text-center">
                    <div>
                      <span className="text-[9px] text-slate-400 font-bold block">신청 전 잔여</span>
                      <span className="font-black text-xs text-slate-900">{beforeBalance.toFixed(1)}일</span>
                    </div>
                    <div className="border-x border-slate-100">
                      <span className="text-[9px] text-indigo-600 font-bold block">이번 신청</span>
                      <span className="font-black text-xs text-indigo-600">{isSpecial ? '0일' : `-${currentRequestDays}일`}</span>
                    </div>
                    <div>
                      <span className="text-[9px] text-emerald-600 font-bold block">승인 후 잔여</span>
                      <span className={`font-black text-xs ${afterBalance < 0 && !isSpecial ? 'text-rose-600' : 'text-emerald-600'}`}>
                        {afterBalance.toFixed(1)}일
                      </span>
                    </div>
                  </div>
                </div>
              )}

              <div className="bg-white border border-slate-200 rounded-2xl p-4 space-y-2.5 text-xs">
                <div className="flex justify-between items-center py-1 border-b border-slate-100">
                  <span className="font-bold text-slate-500">휴가 구분</span>
                  <div className="font-extrabold text-slate-900">
                    {approval.IsSpecial ? (
                      <span className="text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded font-black border border-emerald-200">
                        특별휴가 ({approval.SpecialType})
                      </span>
                    ) : approval.LeaveType === 'HALF_AM' ? (
                      <span className="text-sky-700 bg-sky-50 px-2 py-0.5 rounded font-black border border-sky-200 inline-flex items-center gap-1">
                        <span>☀️</span> 오전반차 (09:00~13:00)
                      </span>
                    ) : approval.LeaveType === 'HALF_PM' ? (
                      <span className="text-amber-700 bg-amber-50 px-2 py-0.5 rounded font-black border border-amber-200 inline-flex items-center gap-1">
                        <span>🌙</span> 오후반차 (14:00~18:00)
                      </span>
                    ) : approval.LeaveType === 'HALF' ? (
                      <span className="text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded font-black border border-indigo-200">
                        반차 (0.5일)
                      </span>
                    ) : approval.LeaveType === 'QUARTER' ? (
                      <span className="text-teal-700 bg-teal-50 px-2 py-0.5 rounded font-black border border-teal-200">
                        반반차 (0.25일)
                      </span>
                    ) : (
                      <span className="text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded font-black border border-indigo-200">
                        연차
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex justify-between items-center py-1 border-b border-slate-100">
                  <span className="font-bold text-slate-500">신청 기간</span>
                  <span className="font-extrabold text-indigo-700">{approval.StartDate} ~ {approval.EndDate || approval.StartDate}</span>
                </div>
                <div className="flex justify-between items-center py-1 border-b border-slate-100">
                  <span className="font-bold text-slate-500">사용 일수</span>
                  <span className="font-extrabold text-rose-600 bg-rose-50 px-2 py-0.5 rounded">{approval.LeaveDays || approval.Days || 0}일 차감</span>
                </div>
                <div className="pt-1">
                  <span className="font-bold text-slate-500 block mb-1">신청 사유</span>
                  <div className="bg-slate-50 p-2.5 rounded-xl border border-slate-200/60 font-medium text-slate-800 whitespace-pre-wrap">
                    {approval.Reason || approval.Details || "(사유 없음)"}
                  </div>
                </div>
                {approval.CancelReason && (
                  <div className="pt-1 bg-rose-50 p-2.5 rounded-xl border border-rose-200">
                    <span className="font-black text-rose-700 block mb-1 flex items-center gap-1">
                      <span>⚠️</span> 연차 취소 신청 사유
                    </span>
                    <div className="bg-white p-2 rounded-lg border border-rose-100 font-medium text-rose-900 whitespace-pre-wrap">
                      {approval.CancelReason}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {isExpense && (
            <div className="bg-white border border-slate-200 rounded-2xl p-4 space-y-2.5 text-xs">
              <div className="flex justify-between items-center py-1 border-b border-slate-100">
                <span className="font-bold text-slate-500">청구 금액</span>
                <span className="font-black text-sm text-indigo-700">{Number(approval.Amount || approval.ExpenseAmount || 0).toLocaleString()}원</span>
              </div>
              <div className="flex justify-between items-center py-1 border-b border-slate-100">
                <span className="font-bold text-slate-500">사용처 / 내용</span>
                <span className="font-extrabold text-slate-900">{approval.Details || approval.ExpenseDetails || "경비 사용"}</span>
              </div>
              <div className="flex justify-between items-center py-1 border-b border-slate-100">
                <span className="font-bold text-slate-500">결제 수단</span>
                <span className="font-medium text-slate-800">{approval.CardCompany || "법인카드"} {approval.CardNumber ? `(${approval.CardNumber})` : ""}</span>
              </div>
              {(approval.ReceiptS3Url || approval.ReceiptUrl || approval.ProofUrl) && (
                <div className="pt-2">
                  <span className="font-bold text-slate-500 block mb-1">영수증 첨부</span>
                  <a
                    href={approval.ReceiptS3Url || approval.ReceiptUrl || approval.ProofUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-xl font-bold transition-colors"
                  >
                    <Receipt className="w-3.5 h-3.5" />
                    <span>영수증 파일 원본 보기 ({approval.ProofFileName || approval.FileName || '영수증 파일'})</span>
                  </a>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Approval Stages / Line */}
        {stages && stages.length > 0 && (
          <div className="space-y-1.5">
            <span className="text-xs font-extrabold text-slate-700">결재 라인 진행 현황</span>
            <div className="flex items-center gap-1.5 overflow-x-auto py-1">
              {stages.map((stage, idx) => {
                const isCurrentActiveStep = Number(stage.Step) === currentStep && (approval.Status === "PENDING" || approval.Status === "CANCEL_REQUESTED");
                return (
                  <React.Fragment key={idx}>
                    <div className={`px-2.5 py-1.5 rounded-xl border text-xs flex flex-col items-center min-w-[76px] transition-all ${
                      stage.Status === "APPROVED" ? "bg-teal-50 border-teal-200 text-teal-800 font-extrabold" :
                      stage.Status === "REJECTED" ? "bg-rose-50 border-rose-200 text-rose-800 font-extrabold" :
                      isCurrentActiveStep ? "bg-amber-100/80 border-amber-400 text-amber-900 font-black ring-2 ring-amber-300 shadow-2xs" :
                      "bg-slate-50 border-slate-200 text-slate-400 font-bold"
                    }`}>
                      <span className="text-[10px] text-slate-500">{stage.Role || `${idx + 1}차`}</span>
                      <span className="truncate max-w-[70px] font-black">{stage.ApproverName || "결재자"}</span>
                      <span className="text-[9px] mt-0.5">
                        {stage.Status === "APPROVED" ? "승인 완료" : 
                         stage.Status === "REJECTED" ? "반려됨" : 
                         isCurrentActiveStep ? "현재 진행" : "대기"}
                      </span>
                    </div>
                    {idx < stages.length - 1 && (
                      <ChevronRight className="w-3 h-3 text-slate-300 flex-shrink-0" />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        )}

        {/* Conditional Footer: Active Action vs Read-Only Waiting */}
        {isTurn ? (
          <>
            {/* Comment / Rejection Reason Input */}
            <div className="space-y-1.5 text-left">
              <label className="text-xs font-bold text-slate-700 flex items-center justify-between">
                <span>승인 의견 / 반려 사유 작성</span>
                <span className="text-[11px] text-slate-400 font-normal">(반려 시 필수 입력)</span>
              </label>
              <textarea
                rows={3}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="결재 승인 코멘트 또는 반려 사유를 입력해 주세요 (신청자에게 카카오톡으로 전달됩니다)..."
                className="w-full px-3.5 py-2.5 text-xs bg-slate-50 border border-slate-300 rounded-2xl focus:outline-none focus:border-indigo-600 focus:bg-white transition-all text-slate-900 placeholder-slate-400"
              />
            </div>

            {errorMessage && (
              <div className="p-3 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-xs font-bold flex items-center gap-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>{errorMessage}</span>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-2.5 pt-2">
              <button
                type="button"
                disabled={isSubmitting}
                onClick={handleReject}
                className="flex-1 py-3 px-4 rounded-xl border border-rose-300 bg-rose-50 hover:bg-rose-100 text-rose-700 font-bold text-sm flex items-center justify-center gap-1.5 transition-all cursor-pointer disabled:opacity-50"
              >
                <X className="w-4 h-4" />
                <span>반려 처리</span>
              </button>
              <button
                type="button"
                disabled={isSubmitting}
                onClick={handleApprove}
                className="flex-2 py-3 px-4 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-extrabold text-sm flex items-center justify-center gap-1.5 shadow-sm transition-all cursor-pointer disabled:opacity-50"
              >
                <Check className="w-4 h-4" />
                <span>{isSubmitting ? "처리 중..." : "승인 확정"}</span>
              </button>
            </div>
          </>
        ) : (
          <div className="space-y-3 pt-2">
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 space-y-1.5 text-center">
              <div className="flex items-center justify-center gap-2 text-amber-800 font-extrabold text-xs sm:text-sm">
                <Clock className="w-4 h-4 text-amber-600" />
                <span>현재 {currentStep}차 [{currentApproverName}] 님의 결재가 진행 중입니다.</span>
              </div>
              <p className="text-[11px] text-amber-700 font-medium">
                앞선 단계 결재권자의 승인이 완료되어 본인 차례가 되면 승인 및 반려 버튼이 자동으로 활성화됩니다.
              </p>
            </div>

            {isApplicant && (approval.Status === 'PENDING' || approval.Status === 'CANCEL_REQUESTED') && (
              <button
                type="button"
                onClick={() => {
                  if (window.confirm('신청하신 결재 건을 즉시 취소(회수)하시겠습니까?')) {
                    if (isExpense) {
                      withdrawExpenseRequest(approval.ApprovalID);
                    } else {
                      withdrawLeaveRequest(approval.ApprovalID);
                    }
                    onClose();
                  }
                }}
                className="w-full py-2.5 px-4 rounded-xl border border-rose-200 bg-rose-50 hover:bg-rose-100 text-rose-700 font-bold text-xs flex items-center justify-center gap-1.5 transition-all cursor-pointer shadow-2xs"
              >
                <span>🗑️ 신청 취소 (결재 회수)</span>
              </button>
            )}

            <button
              type="button"
              onClick={onClose}
              className="w-full py-3 px-4 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-extrabold text-sm transition-all cursor-pointer"
            >
              확인 완료 및 닫기
            </button>
          </div>
        )}

      </div>
    </div>
  );
};

export default ApprovalActionModal;
